import React, {
  useEffect, useMemo,
} from 'react';
import { FormProvider, useForm, FieldValues } from 'react-hook-form';

import { Button } from '@ast/magma/components/button';
import { PinInput } from '@ast/magma/components/pininput';
import { Spinner } from '@ast/magma/components/spinner';

import { PhoneSmsEnrollmentStatus } from '@app/queryTyping';

import { FormControl } from '@app/common/components/controls/FormControl';
import {
  formControlMinLengthRule,
  formControlRequiredRule,
} from '@app/common/components/controls/formControlValidation';
import { useNotifierContext } from '@app/common/components/Notifier';
import { UserFriendlyFragment } from '@app/common/queries/queryTyping/validation-unions';

import { useSettingFieldsContext } from '@app/widgets/contacts-and-settings/contexts/SettingFieldsContext';
import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';

import { useConfirmSmsEnrollmentMutation } from '../../queries/queryTyping/confirmSmsEnrollment';
import { useSendSmsEnrollmentMutation } from '../../queries/queryTyping/sendSmsEnrollment';
import { MappedSubFields } from '../../utils/subFieldsMapper';

import styles from './ConfirmPhone.pcss';
import { useResendSms } from './useResendSms';

export interface ConfirmPhoneProps {
  readonly data: MappedSubFields;
  readonly onComplete: () => void;
  readonly onCancel: () => void;
  readonly onRequestError?: (message: string) => void;
}

const PIN_FIELD_NAME = 'oneTimePassword';
const PIN_FIELD_LENGTH = 6;

export const ConfirmPhone = ({
  data,
  onComplete,
  onCancel,
  onRequestError,
}: ConfirmPhoneProps) => {
  const { t } = useUserSettingsTranslation();
  const { settingsQuery } = useSettingFieldsContext();
  const form = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });
  const { addAlerts, clearAlerts } = useNotifierContext();

  const confirmPhoneValidation = useMemo(
    () => [
      formControlRequiredRule(
        t(
          "confirm-phone.pin-field.required|Message for pin required validation rule on phone details's screen",
          'This field is required',
        ),
      ),
      formControlMinLengthRule(
        PIN_FIELD_LENGTH,
        t(
          "confirm-phone.pin-field.min-length|Message for pin minLength validation rule on phone details's screen",
          'Please complete the field',
        ),
      ),
    ],
    [],
  );

  const [sendSms, { error: sendSmsError, data: sendSmsData }] = useSendSmsEnrollmentMutation();

  const [
    resendSms,
    { error: resendSmsError, data: resendSmsData, loading: resending },
  ] = useResendSms();

  const onSendSms = () => {
    clearAlerts();
    sendSms({
      variables: {
        mobilePhoneId: data.UniqueId?.value || '',
      },
    });
  };

  const onResendSms = () => {
    clearAlerts();
    resendSms({
      variables: {
        mobilePhoneId: data.UniqueId?.value || '',
      },
    });
  };

  const [confirmSms, { error: confirmSmsError }] = useConfirmSmsEnrollmentMutation({
    onCompleted: ({ confirmSmsEnrollment }) => {
      const { errors } = confirmSmsEnrollment || {};

      if (errors?.length) {
        form.setError(PIN_FIELD_NAME, {
          type: 'custom',
          message: (errors[0] as UserFriendlyFragment).errorMessage || '',
        });
      } else {
        settingsQuery.refetch().then(() => {
          if (
            confirmSmsEnrollment?.smsEnrollmentStatus
              === PhoneSmsEnrollmentStatus.ENROLLED
          ) {
            clearAlerts();
            addAlerts([
              {
                level: 'success',
                title: t(
                  'confirm-enroll-phone.successful-message|Successful message for enroll phone',
                  'Your phone number has been successfully enrolled into text messaging. Now you can setup the alerts to be sent to {{phone}}.',
                  {
                    phone: data.MobilePhone?.value || 'your phone',
                  },
                ),
              },
            ]);
          }
          onComplete();
        });
      }
    },
  });

  const onChange = () => {
    form.clearErrors();
  };

  const onSubmit = ({ oneTimePassword }: FieldValues) => {
    form.clearErrors();

    confirmSms({
      variables: {
        mobilePhoneId: data.UniqueId?.value || '',
        oneTimePassword,
      },
    });
  };

  useEffect(() => {
    /**
     * in GQL schema and on front-back,
     * enrollment status not have same letter case.
     * GQL - uppercase
     * Front-back - PascalCase.
     * convert to uppercase for matching.
     */
    const status = (data.SmsEnrollmentStatus?.value || '').toUpperCase();
    if (status !== PhoneSmsEnrollmentStatus.NOTENROLLED) return;

    onSendSms();
  }, []);

  useEffect(() => {
    if (
      resendSmsData
      && resendSmsData?.sendSmsEnrollment?.errors?.length === 0
    ) {
      addAlerts([
        {
          level: 'success',
          title: t(
            'confirm-enroll-phone.resend-code|Successful resened code for enroll phone',
            'We successfully sent you a new verification code',
          ),
        },
      ]);
    }
  }, [resendSmsData]);

  useEffect(() => {
    const smsData = resendSmsData || sendSmsData;
    if (!smsData?.sendSmsEnrollment) return;

    if (smsData.sendSmsEnrollment.errors.length === 0) {
      /**
       * refetch data, cause they are not cached,
       * to display relevant info about phone number.
       */
      settingsQuery.refetch();
      return;
    }

    /**
     * if there is at least one error,
     * we call onRequestError callback.
     */
    if (onRequestError && smsData.sendSmsEnrollment.errors.length > 0) {
      onRequestError(
        (smsData.sendSmsEnrollment.errors[0] as UserFriendlyFragment)
          .errorMessage || '',
      );
    }
  }, [sendSmsData, resendSmsData]);

  if (sendSmsError) throw sendSmsError;
  if (resendSmsError) throw resendSmsError;
  if (confirmSmsError) throw confirmSmsError;

  return (
    <div className={styles.container}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className={styles.text}>
            {t(
              'confirm-phone.text|Text before pin field',
              'We’ve sent a Verification Code to this phone number. Please enter the code you received below.',
            )}
          </div>
          <FormControl
            className={styles.pinFieldControl}
            /**
             * workaround to mark control as invalid and display message
             * in not usual way.
             */
            getErrorMessage={(err) => err?.message || ''}
            name={PIN_FIELD_NAME}
            validations={confirmPhoneValidation}
          >
            <PinInput
              autoFocus
              inputsClassName={styles.pinSegment}
              className={styles.pinField}
              data-stable-name="ConfirmPhonePinInput"
              length={PIN_FIELD_LENGTH}
              type="numeric"
              onChange={onChange}
              invalid={Boolean(form.formState.errors[PIN_FIELD_NAME])}
            />
          </FormControl>
          <div className={styles.resendPin}>
            <div>
              {t(
                'confirm-phone.resend.description|Resend pin description',
                "Didn't receive code?",
              )}
            </div>
            <div className={styles.resendRow}>
              <Button
                look="inlinetext"
                data-stable-name="ConfirmPhoneEnrollmentResend"
                aria-disabled={resending}
                onClick={() => {
                  onResendSms();
                }}
              >
                {t(
                  'confirm-phone.resend.link|Resend link text',
                  'Resend verification code',
                )}
              </Button>
              {resending ? (
                <Spinner className={styles.spinner} size="sm" />
              ) : null}
            </div>
          </div>
          <div className={styles.actions}>
            <Button
              data-stable-name="ConfirmPhoneEnrollmentButton"
              look="primary"
              type="submit"
            >
              {t('confirm-phone.submit.label|Submit button label', 'Verify')}
            </Button>
            <Button
              data-stable-name="CancelPhoneEnrollmentButton"
              look="outlined"
              onClick={() => {
                onCancel();
              }}
            >
              {t('confirm-phone.cancel.label|Cancel button label', 'Cancel')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
